/*
  Nische - auto-loading collection grid.

  Replaces the 1-2-3 pagination with products that append themselves as the
  shopper reaches the end of the current set. No navigation, so the page never
  reloads and never jumps back to the top.

  Three things keep it cheap, because the brief was "no loss of page speed":

    1. It fetches through Shopify's Section Rendering API
       (`?section_id=<section.id>`), which returns the product grid alone -
       measured at 114KB against 230KB for the same page fetched whole. No
       header, no footer, no hero to parse and throw away.
    2. It starts the fetch ~800px before the shopper arrives, so the next set is
       usually already in the DOM by the time they would have seen a gap.
    3. Nothing runs, and nothing is fetched, until the first set has been
       scrolled through. First paint is byte for byte what it was before.

  The numbered links stay in the DOM and are only hidden once this script has
  taken over, so crawlers still follow them and a shopper whose JS failed still
  has working pagination. That is also why the hiding is driven by a
  `data-nis-ready` attribute this file sets, rather than by CSS on `.js`: if the
  script never runs, the links never disappear.
*/
(function () {
  /* A section can render more than once on a page; bind once. */
  if (window.NischeInfiniteScroll) return;
  window.NischeInfiniteScroll = true;

  /* Roughly one viewport of runway. Big enough that the fetch is usually
     finished before the shopper reaches the end, small enough that a browse
     that stops after two rows never triggers it at all. */
  var PREFETCH_MARGIN = '800px 0px';

  var observer = null;

  function q(sel, root) {
    return (root || document).querySelector(sel);
  }

  /*
    Dawn hides `.scroll-trigger` cards and reveals them with an
    IntersectionObserver built at page load - which never sees a card appended
    afterwards. They default to visible, so this is not about them vanishing;
    it is the stagger. `--animation-order` comes from the server, where the
    Liquid loop restarts at 1 on every page, so the last card of page 2 would
    sit behind a 1.2s animation delay while the shopper looks at a gap.

    An appended card is arriving exactly as it scrolls into view, so a reveal
    animation has nothing left to reveal. Strip it and the card is simply there.
  */
  function stripRevealAnimation(el) {
    el.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in');
    el.removeAttribute('data-cascade');
    if (el.style) el.style.removeProperty('--animation-order');
  }

  /*
    The server marks the first two images of every page eager, because on a
    normal page load those two are above the fold. Appended pages are always
    below it, so eager there would mean racing the images the shopper is
    actually looking at.
  */
  function relaxImages(el) {
    el.querySelectorAll('img').forEach(function (img) {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
    });
  }

  function setBusy(state, on) {
    state.busy = on;
    if (state.spinner) state.spinner.hidden = !on;
    if (state.btn && on) state.btn.hidden = true;
  }

  function announce(state, text) {
    if (state.status) state.status.textContent = text;
  }

  function finish(state) {
    if (observer) observer.unobserve(state.el);
    state.el.hidden = true;
  }

  function load(state) {
    if (state.busy || !state.next) return;
    setBusy(state, true);

    /*
      `section_id` needs the REAL section id - inside a JSON template that is
      namespaced (`template--123__product-grid`), not the template key. The
      template key alone returns a 404 with an empty body, which looks exactly
      like a successful empty page if you do not check the status.
    */
    var url;
    try {
      url = new URL(state.next, window.location.origin);
    } catch (e) {
      setBusy(state, false);
      return;
    }
    url.searchParams.set('section_id', state.section);

    fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.getElementById('product-grid');
        /* Re-queried every time: a filter change replaces the whole grid, so a
           reference captured at init would point at a detached node. */
        var grid = document.getElementById('product-grid');
        if (!incoming || !grid) throw new Error('no grid in response');

        var items = Array.prototype.slice.call(incoming.children);
        if (!items.length) {
          finish(state);
          setBusy(state, false);
          return;
        }

        var frag = document.createDocumentFragment();
        items.forEach(function (item) {
          stripRevealAnimation(item);
          relaxImages(item);
          frag.appendChild(item);
        });
        grid.appendChild(frag);

        /*
          Quick-add and the price/badge markup are custom elements, which the
          browser upgrades on insertion by itself. The wishlist heart is not -
          it is a delegated click listener plus stored state, so an appended
          card renders an empty heart for a product the shopper has already
          saved until this runs.
        */
        if (window.NischeWishlist && window.NischeWishlist.hydrate) {
          window.NischeWishlist.hydrate(grid);
        }

        /* The next page's own marker carries the URL after this one; when the
           last page has no `next`, that attribute comes back empty and we stop. */
        var incomingMore = doc.querySelector('[data-nis-more]');
        state.next = incomingMore ? incomingMore.getAttribute('data-nis-next') || '' : '';
        state.page = incomingMore ? incomingMore.getAttribute('data-nis-page') : state.page;

        /*
          Write the cursor back onto the element, not just into this closure.
          The attribute is what anything else reads - a re-init after a DOM
          change would otherwise pick up the ORIGINAL page-2 URL and append a
          set the shopper already has. Keeping the two in step also means the
          markup can be trusted when inspecting a page that misbehaved.
        */
        state.el.setAttribute('data-nis-next', state.next);
        if (state.page) state.el.setAttribute('data-nis-page', state.page);

        announce(state, grid.children.length + ' products shown');
        state.failed = false;
        setBusy(state, false);

        if (!state.next) finish(state);
      })
      .catch(function () {
        /*
          Leave the shopper a way forward rather than a dead scroll: the button
          appears only here, and the numbered links are still in the DOM
          underneath it.
        */
        state.failed = true;
        setBusy(state, false);
        if (state.btn) {
          state.btn.hidden = false;
          state.btn.textContent = state.retryLabel;
        }
        announce(state, 'Could not load more products.');
      });
  }

  function init() {
    var el = q('[data-nis-more]');
    if (!el || el.dataset.nisReady === 'true') return;

    var state = {
      el: el,
      spinner: q('[data-nis-spinner]', el),
      btn: q('[data-nis-btn]', el),
      status: q('[data-nis-status]', el),
      next: el.getAttribute('data-nis-next') || '',
      section: el.getAttribute('data-nis-section'),
      page: el.getAttribute('data-nis-page'),
      retryLabel: el.getAttribute('data-nis-retry') || 'Try again',
      busy: false,
      failed: false,
    };

    /* Only claim the pagination once we know we can actually replace it. */
    var auto = el.getAttribute('data-nis-auto') === 'true' && 'IntersectionObserver' in window;
    if (!auto) {
      /* Without the observer the numbered links remain the real control, so
         leave them visible and do nothing at all. */
      return;
    }

    el.dataset.nisReady = 'true';

    if (state.btn) {
      state.btn.addEventListener('click', function () {
        state.btn.hidden = true;
        load(state);
      });
    }

    if (observer) observer.disconnect();
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !state.failed) load(state);
        });
      },
      { rootMargin: PREFETCH_MARGIN }
    );
    observer.observe(el);
  }

  /*
    Filtering and sorting replace the whole of #ProductGridContainer's innerHTML
    (facets.js), which throws away this marker and puts a fresh one - with the
    filtered result's own next-page URL - in its place. Watching the container
    is what makes "filter, then keep scrolling" work; binding once at load would
    silently stop after the first filter change.
  */
  function watchForRerender() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        init();
      });
    }).observe(container, { childList: true, subtree: true });
  }

  function boot() {
    init();
    watchForRerender();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
